<div class="opportunities-page page-container">
  <!-- Header -->
  <div class="page-header">
    <div class="flex items-center justify-between gap-4">
      <div>
        <h1 class="page-title">Oportunidades</h1>
        <p class="page-subtitle">
          Gestiona y monitorea todas tus oportunidades de negocio en un solo lugar.
        </p>
      </div>
      <button (click)="openCreateModal()" class="app-btn app-btn--primary flex items-center gap-2">
        <fa-icon [icon]="faPlus"></fa-icon>
        <span>Nueva Oportunidad</span>
      </button>
    </div>
  </div>

  <!-- Search & Filters (replaces metrics cards) -->
  <div class="filters-row mb-3">
    <!-- Search & Filters -->
    <div class="filters-bar">
      <!-- Search -->
      <div class="search-wrapper">
        <fa-icon [icon]="faSearch" class="search-icon"></fa-icon>
        <input
          type="text"
          class="search-input app-input"
          placeholder="Buscar por empresa..."
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange($event)"
        />
      </div>

      <!-- Date range -->
      <div class="pill pill--control">
        <app-date-range-picker
          [startDate]="startDate"
          [endDate]="endDate"
          placeholder="Fecha de alta"
          (rangeChange)="onDateRangeChange($event)"
        ></app-date-range-picker>
      </div>

      <!-- Close date range -->
      <div class="pill pill--control">
        <app-date-range-picker
          [startDate]="closeStartDate"
          [endDate]="closeEndDate"
          placeholder="Fecha de cierre"
          panelTitle="Rango de fecha de cierre"
          idPrefix="drp-close"
          (rangeChange)="onCloseDateRangeChange($event)"
        ></app-date-range-picker>
      </div>

      <!-- Business status -->
      <div class="select-wrapper">
        <app-custom-select
          [(ngModel)]="selectedStage"
          (ngModelChange)="onStageChange($event)"
          [options]="stageOptions"
          [small]="true"
        ></app-custom-select>
      </div>

      <!-- Partner filter (Admin only) -->
      @if (isAdmin) {
        <div class="select-wrapper">
          <app-custom-select
            [(ngModel)]="selectedPartner"
            (ngModelChange)="applyFilters()"
            [options]="partnerOptions"
            [icon]="faHandshake"
          ></app-custom-select>
        </div>

        <!-- Partner type filter (Admin only) -->
        <div class="select-wrapper">
          <app-custom-select
            [(ngModel)]="selectedPartnerType"
            (ngModelChange)="applyFilters()"
            [options]="partnerTypeOptions"
            [icon]="faUserTag"
          ></app-custom-select>
        </div>
      }

      <!-- Actions (solo si hay filtros) -->
      @if (hasActiveFilters) {
        <button
          class="btn-reset-filters"
          (click)="clearFilters()"
          type="button"
          title="Limpiar filtros"
        >
          <div class="btn-reset-filters__icon-wrapper">
            <fa-icon [icon]="faFilterCircleXmark" class="btn-reset-filters__icon"></fa-icon>
          </div>
          <span class="btn-reset-filters__text">Limpiar filtros</span>
        </button>
      }
    </div>
  </div>

  <!-- Deals Table -->
  <div class="deals-table-wrapper" (scroll)="onScroll($event)" style="overflow: auto">
    @if (isLoading) {
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Cargando oportunidades...</p>
      </div>
    } @else if (deals.length === 0) {
      <div class="empty-state">
        <fa-icon [icon]="faFileContract" class="empty-icon"></fa-icon>
        <h3>No hay oportunidades</h3>
        <p>No se encontraron oportunidades en el sistema.</p>
      </div>
    } @else {
      <table class="deals-table">
        <thead>
          <tr>
            <th class="col-opportunity sortable" (click)="onSort('opportunity')">
              <div class="header-content">
                <span>Oportunidad</span>
                <fa-icon
                  [icon]="getSortIcon('opportunity')"
                  class="sort-icon"
                  [class.active]="sortBy === 'opportunity'"
                ></fa-icon>
              </div>
            </th>
            <th class="sortable" (click)="onSort('amount')">
              <div class="header-content">
                <span>Importe</span>
                <fa-icon
                  [icon]="getSortIcon('amount')"
                  class="sort-icon"
                  [class.active]="sortBy === 'amount'"
                ></fa-icon>
              </div>
            </th>
            <th class="sortable" (click)="onSort('status')">
              <div class="header-content">
                <span>Estado</span>
                <fa-icon
                  [icon]="getSortIcon('status')"
                  class="sort-icon"
                  [class.active]="sortBy === 'status'"
                ></fa-icon>
              </div>
            </th>
            @if (isAdmin) {
              <th class="col-partner sortable" (click)="onSort('referenciador')">
                <div class="header-content">
                  <span>Partner</span>
                  <fa-icon
                    [icon]="getSortIcon('referenciador')"
                    class="sort-icon"
                    [class.active]="sortBy === 'referenciador'"
                  ></fa-icon>
                </div>
              </th>
              <th class="col-partner-type sortable" (click)="onSort('partner_type')">
                <div class="header-content">
                  <span>Tipo de Partner</span>
                  <fa-icon
                    [icon]="getSortIcon('partner_type')"
                    class="sort-icon"
                    [class.active]="sortBy === 'partner_type'"
                  ></fa-icon>
                </div>
              </th>
            }

            <th class="col-date sortable" (click)="onSort('createdate')">
              <div class="header-content">
                <span>Fecha de alta</span>
                <fa-icon
                  [icon]="getSortIcon('createdate')"
                  class="sort-icon"
                  [class.active]="sortBy === 'createdate'"
                ></fa-icon>
              </div>
            </th>
            <th class="col-date sortable" (click)="onSort('closedate')">
              <div class="header-content">
                <span>Fecha de cierre</span>
                <fa-icon
                  [icon]="getSortIcon('closedate')"
                  class="sort-icon"
                  [class.active]="sortBy === 'closedate'"
                ></fa-icon>
              </div>
            </th>
            <th class="text-right">Actividad</th>
          </tr>
        </thead>
        <tbody>
          @for (deal of deals; track deal.id) {
            <tr>
              <td class="col-opportunity">
                <div class="deal-info">
                  <div
                    class="deal-title truncate-with-tooltip"
                    [attr.title]="deal.dealname"
                    [attr.data-full]="deal.dealname"
                  >
                    {{ deal.dealname }}
                  </div>
                </div>
              </td>
              <td>
                <div class="deal-value">{{ formatCurrency(+deal.amount) }}</div>
              </td>
              <td>
                <div class="stage-cell">
                  <span class="stage-badge" [ngClass]="getStageClass(deal.dealstage)">
                    {{ getStageLabel(deal.dealstage) }}
                  </span>
                  @if (getStageClass(deal.dealstage) === 'stage-lost' && deal.closed_lost_reason) {
                    <button
                      class="lost-reason-btn"
                      (click)="showLostReason(deal)"
                      type="button"
                      title="Ver motivo de pérdida"
                    >
                      <fa-icon [icon]="faCircleInfo" class="lost-reason-icon"></fa-icon>
                    </button>
                  }
                </div>
              </td>
              @if (isAdmin) {
                <td class="col-partner">
                  <div class="company-info">
                    <div
                      class="truncate max-w-[140px]"
                      [attr.title]="deal.partner_name || deal.referenciador || '-'"
                      [attr.data-full]="deal.partner_name || deal.referenciador || '-'"
                    >
                      {{ deal.partner_name || deal.referenciador || '-' }}
                    </div>
                  </div>
                </td>
                <td class="col-partner-type">
                  <div
                    class="truncate max-w-[160px]"
                    [attr.title]="deal.partner_type || '-'"
                    [attr.data-full]="deal.partner_type || '-'"
                  >
                    {{ deal.partner_type || '-' }}
                  </div>
                </td>
              }

              <td>
                <div class="date-info">
                  <fa-icon [icon]="faCalendar" class="date-icon"></fa-icon>
                  <span class="date-text">{{ formatDate(deal.createdate) }}</span>
                </div>
              </td>
              <td>
                @if (deal.closedate) {
                  <div class="date-info">
                    <fa-icon [icon]="faCalendar" class="date-icon"></fa-icon>
                    <span class="date-text">{{ formatDate(deal.closedate) }}</span>
                  </div>
                }
              </td>
              <td class="text-right">
                <button
                  type="button"
                  (click)="showActivity(deal)"
                  class="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Ver actividad"
                >
                  <fa-icon [icon]="faMagnifyingGlassPlus" class="text-lg"></fa-icon>
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
      @if (isLoadingMore) {
        <div class="infinite-loader">
          <div class="loading-spinner small"></div>
          <p>Cargando...</p>
        </div>
      }
    }
  </div>

  @if (selectedDealForActivity) {
    <app-deal-activity-modal
      [dealId]="selectedDealForActivity.id"
      [dealName]="selectedDealForActivity.name"
      (close)="closeActivity()"
    ></app-deal-activity-modal>
  }

  @if (showCreateModal) {
    <app-create-opportunity-modal
      (close)="closeCreateModal()"
      (success)="onCreateSuccess()"
    ></app-create-opportunity-modal>
  }
</div>
